import React from "react";
import { BlockMath, InlineMath } from "react-katex";

const boxStyle = {
  borderLeft: "4px solid #ff9900",
  background: "color-mix(in srgb, var(--color-surface, #0b0b3b) 88%, #ff9900 12%)",
  padding: "14px 18px",
  margin: "16px 0",
  borderRadius: 8,
};

export default function DivisibilityProofsPreparationTheory() {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        W dowodzie podzielności nie wystarczy sprawdzić kilku przykładów. Trzeba przekształcić
        wyrażenie do postaci <InlineMath math="m\cdot k" />, gdzie
        <InlineMath math=" k\in\mathbb{Z}" />. Wtedy jest ono podzielne przez
        <InlineMath math=" m" /> dla każdej dopuszczalnej liczby.
      </p>

      <h3>Zapis liczby podzielnej i liczby z resztą</h3>
      <BlockMath math="a=mk,\qquad k\in\mathbb{Z}" />
      <p>
        Tak zapisujemy dowolną liczbę całkowitą podzielną przez <InlineMath math="m" />.
        Jeśli przy dzieleniu przez <InlineMath math="m" /> liczba daje resztę
        <InlineMath math=" r" />, zapisujemy:
      </p>
      <BlockMath math="a=mk+r,\qquad k\in\mathbb{Z},\qquad 0\le r<m" />

      <div style={boxStyle}>
        <strong>Uwaga na resztę.</strong> Przy dzieleniu przez <InlineMath math="3" /> reszta może
        wynosić tylko <InlineMath math="0" />, <InlineMath math="1" /> albo <InlineMath math="2" />.
        „Reszta <InlineMath math="5" />” nie jest poprawnym wynikiem dzielenia z resztą;
        <InlineMath math=" 5=3+2" />, więc odpowiada ona reszcie <InlineMath math="2" />.
      </div>

      <h3>Niezależne liczby — niezależne parametry</h3>
      <p>
        Niech liczba <InlineMath math="a" /> daje przy dzieleniu przez <InlineMath math="3" />
        resztę <InlineMath math="1" />, a liczba <InlineMath math="b" /> resztę
        <InlineMath math="2" />. Poprawny zapis to:
      </p>
      <BlockMath math="a=3x+1,\qquad b=3y+2,\qquad x,y\in\mathbb{Z}" />
      <p>
        Nie zapisujemy obu liczb za pomocą tego samego parametru, np.
        <InlineMath math=" a=3x+1" /> i <InlineMath math="b=3x+2" />. Taki zapis wymuszałby
        dodatkowo <InlineMath math="b-a=1" />, chociaż zadanie niczego takiego nie zakłada.
      </p>

      <h3>Schemat dowodu algebraicznego</h3>
      <ol style={{ paddingLeft: 24 }}>
        <li>Zapisz liczby zgodnie z warunkiem podzielności lub reszty.</li>
        <li>Podstaw je do badanego wyrażenia.</li>
        <li>Uprość wyrażenie i wyłącz oczekiwany dzielnik przed nawias.</li>
        <li>Uzasadnij, że zawartość nawiasu jest liczbą całkowitą.</li>
      </ol>

      <p><strong>Przykład.</strong> Dla liczb <InlineMath math="a=3x+1" /> i <InlineMath math="b=3y+2" />:</p>
      <BlockMath math="a+b=(3x+1)+(3y+2)=3x+3y+3=3(x+y+1)" />
      <p>
        Ponieważ <InlineMath math="x+y+1\in\mathbb{Z}" />, suma <InlineMath math="a+b" />
        jest podzielna przez <InlineMath math="3" />.
      </p>

      <h3>Iloczyn kolejnych liczb</h3>
      <p>
        Czasem zamiast podstawiania wystarczy zauważyć, że wśród kolejnych liczb zawsze znajduje
        się wielokrotność danego dzielnika. Na przykład:
      </p>
      <BlockMath math="n^3-n=n(n^2-1)=n(n-1)(n+1)" />
      <p>
        Jest to iloczyn trzech kolejnych liczb całkowitych, więc jedna z nich jest podzielna przez
        <InlineMath math=" 3" />. Dlatego całe wyrażenie jest podzielne przez <InlineMath math="3" />.
      </p>

      <div style={boxStyle}>
        <strong>Najczęstsze błędy:</strong>
        <ul style={{ margin: "8px 0 0", paddingLeft: 22 }}>
          <li>użycie jednego parametru dla dwóch niezależnych liczb,</li>
          <li>sprawdzenie kilku wartości zamiast dowodu dla dowolnej liczby,</li>
          <li>zakończenie rachunku bez pokazania czynnika będącego dzielnikiem,</li>
          <li>pominięcie informacji, że pozostały czynnik należy do <InlineMath math="\mathbb{Z}" />.</li>
        </ul>
      </div>
    </div>
  );
}
